import { z } from 'zod';
import { Email, HexColor, Id, IsoDate, Password } from './common';
import { PERMISSIONS, PLATFORM_PERMISSIONS } from './rbac';
import { FeatureKeys, LimitKeys, PlatformRole, SubscriptionStatus, ThemeMode, UserStatus } from './enums';
/* ============================================================================
   Auth & account flows (PLAN §5). Access token: 15 min JWT kept in memory.
   Refresh token: httpOnly cookie scoped to the tenant host, rotated on use.
   ========================================================================== */
export const LoginSchema = z.object({
    email: Email,
    password: z.string().min(1).max(128),
    remember: z.boolean().default(true),
});
/** Signed-in user + tenant context returned by login/refresh/me. */
export const MeSchema = z.object({
    user: z.object({
        id: Id,
        email: z.string(),
        name: z.string(),
        avatarUrl: z.string().nullable(),
        initials: z.string(),
        color: HexColor,
        status: z.enum(UserStatus),
        timezone: z.string(),
        themeMode: z.enum(ThemeMode),
        pendingDeletion: z.object({ scheduledFor: IsoDate }).nullable(),
        pendingEmail: z.string().nullable(),
    }),
    tenant: z.object({ id: Id, slug: z.string(), name: z.string() }),
    role: z.object({ id: Id, key: z.string(), name: z.string() }),
    permissions: z.array(z.enum(PERMISSIONS)),
    entitlements: z.object({
        limits: z.record(z.enum(LimitKeys), z.number().int()),
        features: z.record(z.enum(FeatureKeys), z.boolean()),
    }),
    subscription: z.object({ status: z.enum(SubscriptionStatus), planName: z.string(), endsAt: IsoDate }),
    workspaces: z.array(z.object({ id: Id, name: z.string(), slug: z.string(), roleKey: z.string() })),
    /** set when DevNinjaWeb staff are inside this tenant ("Enter as DevNinjaWeb") */
    impersonatedBy: z.object({ name: z.string(), email: z.string() }).nullable(),
});
export const AuthSessionSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int().positive(),
    me: MeSchema,
});
export const RefreshResponseSchema = z.object({ accessToken: z.string(), expiresIn: z.number().int() });
/**
 * Forgot password. 422 validation_failed (fields.email) when there's no active account with that email in this
 * workspace; 429 rate_limited within the resend cooldown. `delivery` says whether the email really left:
 * `not_configured` = email isn't set up yet (don't tell people to check their inbox).
 */
export const ForgotPasswordSchema = z.object({ email: Email });
export const EmailDelivery = ['sent', 'queued', 'not_configured', 'failed'];
export const ForgotPasswordResponseSchema = z.object({
    ok: z.literal(true),
    resendAfterSeconds: z.number().int(),
    delivery: z.enum(EmailDelivery),
});
export const ResetTokenCheckSchema = z.object({ token: z.string().min(20) });
export const ResetTokenStateSchema = z.object({
    state: z.enum(['valid', 'expired', 'used', 'invalid']),
    maskedEmail: z.string().nullable(),
});
export const ResetPasswordSchema = z.object({ token: z.string().min(20), password: Password });
export const InvitePreviewSchema = z.object({
    state: z.enum(['valid', 'expired', 'revoked', 'accepted', 'invalid']),
    email: z.string().nullable(),
    tenantName: z.string().nullable(),
    inviterName: z.string().nullable(),
    roleName: z.string().nullable(),
    workspaces: z.array(z.string()),
});
export const AcceptInviteSchema = z.object({
    token: z.string().min(20),
    name: z.string().trim().min(2).max(80),
    password: Password,
    acceptTerms: z.literal(true),
});
export const ChangePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: Password,
    signOutOthers: z.boolean().default(true),
});
export const ChangeEmailSchema = z.object({ newEmail: Email, password: z.string().min(1) });
export const ConfirmEmailSchema = z.object({ token: z.string().min(20) });
export const SessionInfoSchema = z.object({
    id: Id,
    device: z.string(),
    browser: z.string(),
    ip: z.string().nullable(),
    location: z.string().nullable(),
    current: z.boolean(),
    lastSeenAt: IsoDate,
    createdAt: IsoDate,
});
export const DeleteAccountSchema = z.object({ confirmEmail: Email, password: z.string().min(1) });
export const DeletionScheduledSchema = z.object({ scheduledFor: IsoDate });
export const ProfileUpdateSchema = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    avatarUrl: z.string().url().nullable().optional(),
    timezone: z.string().max(60).optional(),
    themeMode: z.enum(ThemeMode).optional(),
});
export const TransferOwnershipSchema = z.object({ toUserId: Id, password: z.string().min(1) });
/* ---------- Mission HQ (platform) auth: password + mandatory TOTP ---------- */
export const PlatformLoginSchema = z.object({ email: Email, password: z.string().min(1).max(128) });
/** Step 1 result: either a session (TOTP off, first login) or a short-lived challenge for TOTP. */
export const PlatformLoginResponseSchema = z.discriminatedUnion('step', [
    z.object({ step: z.literal('totp'), challengeId: z.string(), expiresIn: z.number().int() }),
    z.object({ step: z.literal('done'), accessToken: z.string(), expiresIn: z.number().int() }),
]);
/** `code`: 6-digit authenticator code, or a one-time recovery code `XXXX-XXXX` (added by API-A, backward compatible). */
export const PlatformTotpVerifySchema = z.object({
    challengeId: z.string(),
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^(\d{6}|[A-Z0-9]{4}-[A-Z0-9]{4})$/, 'Enter the 6-digit code or a recovery code'),
});
export const PlatformMeSchema = z.object({
    id: Id,
    email: z.string(),
    name: z.string(),
    role: z.enum(PlatformRole),
    permissions: z.array(z.enum(PLATFORM_PERMISSIONS)),
    totpEnabled: z.boolean(),
    lastLoginAt: IsoDate.nullable(),
});
export const PlatformSessionSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int(),
    me: PlatformMeSchema,
});
/** One-time setup link (seed prints it) → set password, then enrol TOTP. */
export const PlatformSetupPreviewSchema = z.object({
    state: z.enum(['valid', 'expired', 'used', 'invalid']),
    email: z.string().nullable(),
    name: z.string().nullable(),
});
export const PlatformSetupSchema = z.object({ token: z.string().min(20), name: z.string().min(2), password: Password });
export const TotpEnrolSchema = z.object({
    secret: z.string(),
    otpauthUrl: z.string(),
    /** QR matrix, rendered client-side */
    qr: z.array(z.array(z.boolean())),
    /** One-time recovery codes, shown once; valid after the code is confirmed (added by API-A, optional). */
    recoveryCodes: z.array(z.string()).optional(),
});
export const TotpConfirmSchema = z.object({ code: z.string().regex(/^\d{6}$/) });
//# sourceMappingURL=auth.js.map